"use client";

/**
 * B17 walk-away tracker (2026-07-18).
 *
 * Records the shopper who looked at a comparison and left WITHOUT acting:
 * no Buy, no Negotiate, no Share, no Watch. Carries the best price they saw,
 * so we can later answer "at what price do people walk away?" (F53).
 *
 * Why this is a first-class event rather than something inferred later:
 * a walk-away is the ABSENCE of a click, and absence cannot be reconstructed
 * from history once the session is gone. Every day it is not captured is
 * permanently lost, which is why it is one of the three signals flagged as
 * un-recreatable.
 *
 * How it fires:
 *   - `visibilitychange` -> hidden covers tab close, tab switch, and mobile
 *     backgrounding (the common exits).
 *   - `pagehide` covers navigation away and bfcache.
 *   - `navigator.sendBeacon` (inside the analytics helper) is what makes the
 *     request survive the page going away; a normal fetch would be cancelled.
 *
 * Fires at most ONCE per view, and only when there was actually something to
 * act on (>=1 retailer), so an empty/failed search is never counted as a
 * walk-away.
 *
 * Renders nothing.
 */

import { useEffect, useRef } from "react";

import {
  hasComparisonAction,
  resetComparisonAction,
  trackComparisonWalkedAway,
} from "@/lib/analytics";
import { carryWalkAwayClock, takeSearchHandoff } from "@/lib/searchHandoff";

type PageKind =
  | "home"
  | "product"
  | "list"
  | "comparison"
  | "compare_streaming"
  | "other";

interface Props {
  productSlug: string | null;
  pageKind: PageKind;
  /** Retailers actually shown. 0/null means nothing to act on -> never fires. */
  retailerCount: number | null;
  /** Best (lowest) True Final Price on screen, the number they walked away from. */
  lowestTfpAud: number | null;
}

export function WalkAwayTracker({
  productSlug,
  pageKind,
  retailerCount,
  lowestTfpAud,
}: Props) {
  // Latest values in a ref so the effect can subscribe ONCE on mount instead
  // of re-subscribing whenever a parent re-renders (the streaming page
  // re-renders on every retailer that lands).
  const dataRef = useRef({ productSlug, pageKind, retailerCount, lowestTfpAud });
  dataRef.current = { productSlug, pageKind, retailerCount, lowestTfpAud };

  const firedRef = useRef(false);
  const startedAtRef = useRef<number>(0);
  const maxScrollPctRef = useRef(0);
  const mountedAtRef = useRef<number>(0);

  useEffect(() => {
    mountedAtRef.current = Date.now();
    // B-290 item 2 (2026-09-28): a finished search switching to the page it
    // saved is the SAME view continuing - keep its clock, how far down the
    // shopper got, whether they already acted (the module-level action flag
    // is simply not reset) and whether its walk-away was already recorded.
    const carried = productSlug ? takeSearchHandoff(productSlug, "walk_away") : null;
    if (carried) {
      firedRef.current = carried.walkAwayRecorded;
      startedAtRef.current = carried.viewStartedAt ?? mountedAtRef.current;
      maxScrollPctRef.current = carried.maxScrollPct;
    } else {
      // New comparison view: this shopper has not acted yet.
      resetComparisonAction();
      firedRef.current = false;
      startedAtRef.current = mountedAtRef.current;
      maxScrollPctRef.current = 0;
    }

    const measureScroll = () => {
      try {
        const doc = document.documentElement;
        const total = doc.scrollHeight;
        if (!total) return;
        const seen = window.scrollY + window.innerHeight;
        const pct = Math.round((seen / total) * 100);
        const clamped = Math.max(0, Math.min(100, pct));
        if (clamped > maxScrollPctRef.current) {
          maxScrollPctRef.current = clamped;
        }
      } catch {
        // Never let measurement break the page.
      }
    };
    measureScroll(); // above-the-fold share, before any scrolling

    const maybeFire = () => {
      if (firedRef.current) return;
      // Acted on -> by definition not a walk-away.
      if (hasComparisonAction()) return;
      const { productSlug: slug, pageKind: kind, retailerCount: count, lowestTfpAud: tfp } =
        dataRef.current;
      // Nothing was on screen to act on; not a walk-away.
      if (!count || count <= 0) return;
      firedRef.current = true;
      trackComparisonWalkedAway({
        productSlug: slug,
        pageKind: kind,
        retailerCount: count,
        lowestTfpAud: tfp,
        dwellMs: Math.max(0, Date.now() - startedAtRef.current),
        scrollPct: maxScrollPctRef.current,
      });
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") maybeFire();
    };

    window.addEventListener("scroll", measureScroll, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", maybeFire);

    return () => {
      window.removeEventListener("scroll", measureScroll);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", maybeFire);
      // B-290 item 2: the search screen switching to its saved page is not
      // an exit - hand this view over to the saved page instead of firing.
      if (
        carryWalkAwayClock(mountedAtRef.current, {
          viewStartedAt: startedAtRef.current,
          maxScrollPct: maxScrollPctRef.current,
          walkAwayRecorded: firedRef.current,
        })
      ) {
        return;
      }
      // Client-side route change away from the comparison is also an exit.
      maybeFire();
    };
    // Mount-only: values are read from dataRef at fire time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
